import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, Send, User, Users, Copy, Share2, Smile, X, MoreHorizontal, Trash2, ArrowLeft, ImagePlus, Paperclip, FileText } from 'lucide-react';
import { useChannelList, useConversation, useSendMessage, useToggleReaction, useDeleteMessage } from '../../../hooks/useChat';
import { resolveDynamicImageUrl } from '../../../lib/imageUrl';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import toast from 'react-hot-toast';
import type { UserProfile } from '../../../types/auth';

const Pesan = ({ userProfile }: { userProfile: UserProfile | null }) => {
  const { projects } = useOwnerManpro();
  const [searchParams, setSearchParams] = useSearchParams();
  const channelFromUrl = searchParams.get('channel_id');

  const { data: channels = [], isLoading: isLoadingChannels } = useChannelList();

  const [activeChannelId, setActiveChannelId] = useState<string>(channelFromUrl || '');
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  // Set first channel as active by default if not set, or update URL if active changes
  useEffect(() => {
    if (channelFromUrl && channelFromUrl !== activeChannelId) {
      setActiveChannelId(channelFromUrl);
    } else if (channels.length > 0 && !activeChannelId) {
      if (window.innerWidth >= 1024) {
        setActiveChannelId(channels[0].id);
        setSearchParams({ channel_id: channels[0].id }, { replace: true });
      }
    }
  }, [channels, activeChannelId, channelFromUrl, setSearchParams]);

  const handleChannelSelect = (id: string) => {
    setActiveChannelId(id);
    setSearchParams({ channel_id: id }, { replace: true });
  };

  const { data: conversation = [], isLoading: isLoadingConversation } = useConversation(activeChannelId);
  const sendMessageMutation = useSendMessage(userProfile?.id);
  const toggleReactionMutation = useToggleReaction();
  const deleteMessageMutation = useDeleteMessage();

  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [replyingToMessage, setReplyingToMessage] = useState<any | null>(null);
  const [showEmojiPickerFor, setShowEmojiPickerFor] = useState<string | null>(null);

  const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢'];

  const handleMouseEnter = (messageId: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredMessageId(messageId);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMessageId(null);
      setShowEmojiPickerFor(null);
    }, 1000);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Pesan disalin!');
  };

  const handleReaction = (messageId: string, emoji: string) => {
    if (!activeChannelId) return;
    toggleReactionMutation.mutate({ channel_id: activeChannelId, message_id: messageId, emoji });
    setShowEmojiPickerFor(null);
    setHoveredMessageId(null);
  };

  const handleDelete = (messageId: string) => {
    if (confirm('Yakin ingin menghapus pesan ini?')) {
      deleteMessageMutation.mutate(messageId, {
        onSuccess: () => {
          toast.success('Pesan dihapus');
        }
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto scroll when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const handleSendMessage = () => {
    if (!activeChannelId) return;
    if (!message.trim() && selectedFiles.length === 0) return;
    
    sendMessageMutation.mutate(
      {
        channel_id: activeChannelId,
        message: message.trim(),
        reply_to_id: replyingToMessage ? replyingToMessage.id : undefined,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      },
      {
        onSuccess: () => {
          setMessage('');
          setReplyingToMessage(null);
          setSelectedFiles([]);
        },
      }
    );
  };

  const removeFile = (indexToRemove: number) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const activeChannel = channels.find((c) => c.id === activeChannelId);
  const activeTargetUser = activeChannel?.channel_users.find(
    (cu) => cu.user?.id && activeChannelId // fallback logic
  )?.user; // Note: In production, ideally filter by checking against our own user ID. Since we only have 2 users in chat, just pick the other one if we know ours. But since we don't, we can just display whoever we are chatting with by finding the one that is NOT us. Since backend gives us `user_id` in messages, `isMe = msg.user_id !== activeTargetUser.id`.

  // Wait, activeTargetUser is the vendor. How do we find the vendor reliably?
  // We can just pick the first channel_user that is not the currently logged in user.
  const authUserId = userProfile?.id;
  const actualTargetUser = activeChannel?.channel_users.find((cu) => cu.user?.provider)?.user || activeChannel?.channel_users.find((cu) => String(cu.user_id) !== String(authUserId))?.user || activeChannel?.channel_users[0]?.user;

  const activeProject = activeChannel ? projects.find(p => p.id === activeChannel.reference_id || p.manpro_project_id === activeChannel.reference_id) : null;
  const isGroupActive = activeChannel?.reference_type === 'manpro_project';
  const activeTitle = isGroupActive 
    ? `Grup: ${activeProject?.title || 'Proyek'}` 
    : (actualTargetUser ? actualTargetUser.provider?.company_name || `${actualTargetUser.first_name} ${actualTargetUser.last_name || ''}` : 'Sistem');

  return (
    <div className="flex flex-col h-[calc(100vh)] lg:h-[calc(100vh)] -mb-6 space-y-0 lg:space-y-4 -mt-2 lg:mt-0">
      <div className={`shrink-0 px-4 py-3 lg:p-0 ${activeChannelId ? 'hidden lg:block' : 'block'}`}>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-800">Pesan</h1>
        <p className="mt-1 text-xs lg:text-sm text-slate-500">
          Obrolan dengan vendor dan teknisi proyek Anda.
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden lg:rounded-lg border-y lg:border border-slate-200 bg-white shadow-sm">
        <aside className={`flex-col w-full lg:w-[320px] border-r border-slate-200 bg-white ${activeChannelId ? 'hidden lg:flex' : 'flex'}`}>
          <div className="border-b border-slate-100 p-4 shrink-0">
            <p className="text-sm font-bold text-slate-800">Daftar Obrolan</p>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {isLoadingChannels ? (
              <div className="p-4 text-center text-sm text-slate-500">Memuat...</div>
            ) : channels.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">Belum ada obrolan</div>
            ) : channels.map((channel) => {
              const targetUser = channel.channel_users.find((cu) => cu.user?.provider)?.user || channel.channel_users.find((cu) => String(cu.user_id) !== String(authUserId))?.user || channel.channel_users[0]?.user;
              const channelProject = projects.find(p => p.id === channel.reference_id || p.manpro_project_id === channel.reference_id);
              
              const isGroup = channel.reference_type === 'manpro_project';
              const title = isGroup 
                ? `Grup: ${channelProject?.title || 'Proyek'}`
                : (targetUser
                    ? targetUser.provider?.company_name || `${targetUser.first_name} ${targetUser.last_name}`
                    : 'Sistem');

              return (
                <button
                  key={channel.id}
                  onClick={() => handleChannelSelect(channel.id)}
                  className={`block w-full px-4 py-3 text-left transition relative border-b border-slate-50 last:border-b-0 ${activeChannelId === channel.id ? 'bg-blue-50' : 'hover:bg-slate-50'
                    }`}
                >
                  {channel.is_read === 0 && (
                    <span className="absolute right-4 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-blue-600"></span>
                  )}
                  <p className="truncate text-sm font-bold text-slate-800 pr-4">{title}</p>
                  {channelProject && (
                    <p className="mt-0.5 truncate text-xs font-semibold text-blue-600 pr-4">
                      Proyek: {channelProject.title}
                    </p>
                  )}
                  <p className="mt-1.5 truncate text-xs text-slate-500 pr-4">
                    {channel.last_message_sent_user ? `${channel.last_message_sent_user}: ` : ''}
                    {channel.last_sent_attachment_type ? '[Lampiran]' : channel.last_sent_message || 'Belum ada pesan'}
                  </p>
                </button>
              );
            })
            }
          </div>
        </aside>

        <section className={`flex-col flex-1 relative bg-slate-50/30 ${activeChannelId ? 'flex' : 'hidden lg:flex'}`}>
          {activeChannelId ? (
            <>
              <div className="border-b border-slate-100 p-3 lg:p-4 shrink-0 bg-white shadow-sm z-10 flex items-center gap-3">
                <button
                  onClick={() => {
                    setActiveChannelId('');
                    setSearchParams({});
                  }}
                  className="lg:hidden p-2 -ml-2 text-slate-500 hover:bg-slate-100 rounded-full"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 overflow-hidden">
                    {isGroupActive ? (
                      <div className="flex -space-x-2">
                        <Users size={20} />
                      </div>
                    ) : actualTargetUser?.profile_image ? (
                      <img src={resolveDynamicImageUrl((actualTargetUser as any).profile_image_full_path)} alt="Profile" className="h-full w-full object-cover" />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-slate-800 text-lg truncate flex items-center gap-2">
                      {activeTitle}
                    </h2>
                    {activeProject && (
                      <p className="text-xs text-blue-600 truncate font-medium">
                        Proyek: {activeProject.title}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 flex flex-col relative">
                {isLoadingConversation ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    Memuat obrolan...
                  </div>
                ) : conversation.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    Mulai percakapan
                  </div>
                ) : (
                  conversation.map((item) => {
                    const isMe = authUserId ? String(item.user_id) === String(authUserId) : actualTargetUser ? String(item.user_id) !== String(actualTargetUser.id) : false;
                    const isSystem = item.user_id === 'system';
                    const repliedMsg = item.reply_to_id ? conversation.find((msg: any) => String(msg.id) === String(item.reply_to_id)) : null;

                    if (isSystem) {
                      return (
                        <div key={item.id} className="flex justify-center">
                          <div className="rounded-lg bg-slate-200 px-3 py-1.5 text-xs text-slate-600 text-center max-w-[80%]">
                            {item.message}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={item.id}
                        className={`flex ${isMe ? 'justify-end' : 'justify-start'} relative group mt-4 w-full`}
                        onMouseEnter={() => handleMouseEnter(item.id)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {/* Hover actions (not me) */}
                        {!isMe && hoveredMessageId === item.id && (
                          <div className="absolute left-10 -top-6 flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-sm z-10">
                            <button onClick={() => setReplyingToMessage(item)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-full" title="Balas"><MessageSquare size={16} /></button>
                            <div className="relative">
                              <button onClick={() => setShowEmojiPickerFor(item.id)} className="p-1.5 text-slate-400 hover:text-yellow-500 hover:bg-slate-50 rounded-full" title="Reaksi"><Smile size={16} /></button>
                              {showEmojiPickerFor === item.id && (
                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex gap-1 rounded-full bg-white shadow-md border border-slate-200 px-2 py-1 z-20">
                                  {EMOJI_LIST.map((emoji) => (
                                    <button key={emoji} onClick={() => handleReaction(item.id, emoji)} className="hover:scale-125 transition-transform text-lg">{emoji}</button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button onClick={() => handleCopy(item.message)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-full" title="Salin"><Copy size={16} /></button>
                            <button onClick={() => toast('Fitur Bagikan segera hadir')} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-full" title="Bagikan"><Share2 size={16} /></button>
                          </div>
                        )}

                        {/* Hover actions (is me) */}
                        {isMe && hoveredMessageId === item.id && (
                          <div className="absolute right-10 -top-6 flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-sm z-10">
                            <button onClick={() => setReplyingToMessage(item)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-full" title="Balas"><MessageSquare size={16} /></button>
                            <button onClick={() => handleCopy(item.message)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-full" title="Salin"><Copy size={16} /></button>
                            <button onClick={() => handleDelete(item.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full" title="Hapus"><Trash2 size={16} /></button>
                          </div>
                        )}

                        <div className={`flex gap-3 max-w-[85%] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                          {/* Avatar */}
                          <div className="flex-shrink-0 mt-auto mb-1">
                            {(item.user as any)?.profile_image_full_path || item.user?.profile_image ? (
                              <img 
                                src={resolveDynamicImageUrl((item.user as any)?.profile_image_full_path || item.user?.profile_image)} 
                                alt={item.user?.first_name} 
                                className="w-8 h-8 rounded-full object-cover border border-slate-200"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                <User size={16} />
                              </div>
                            )}
                          </div>

                          <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                            {/* Name */}
                            <span className="text-[10px] font-semibold text-slate-500 mb-1 px-1">
                              {item.user ? `${item.user.first_name} ${item.user.last_name || ''}` : 'Unknown User'}
                            </span>

                            <div className={`rounded-2xl px-4 py-2 ${isMe ? 'bg-blue-600 text-white rounded-br-sm shadow-sm' : 'bg-white text-slate-800 rounded-bl-sm border border-slate-200 shadow-sm'}`}>
                              {repliedMsg && (
                                <div className={`mb-2 rounded pl-2 text-xs border-l-2 ${isMe ? 'border-white/50 bg-white/10' : 'border-slate-300 bg-slate-50'} p-1.5`}>
                                  <p className="font-semibold opacity-75">{repliedMsg.user?.first_name || 'User'}</p>
                                  <p className="line-clamp-2 opacity-90">{repliedMsg.message}</p>
                                </div>
                              )}
                              
                              {item.conversation_files?.length ? (
                                <div className="mb-2 flex flex-col gap-2">
                                  {item.conversation_files.map((file: any) => {
                                    const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(file.file_type?.toLowerCase() || '');
                                    const filePath = file.stored_file_name_full_path || file.file_path || file.stored_file_name;
                                    
                                    if (isImage) {
                                      return (
                                        <a href={resolveDynamicImageUrl(filePath)} download={file.original_file_name} target="_blank" rel="noreferrer" key={file.id}>
                                          <img src={resolveDynamicImageUrl(filePath)} alt={file.original_file_name} className="max-w-full rounded-lg max-h-48 object-contain bg-black/5" />
                                        </a>
                                      );
                                    }
                                    return (
                                      <a key={file.id} href={resolveDynamicImageUrl(filePath)} download={file.original_file_name} target="_blank" rel="noreferrer" className={`flex items-center gap-2 p-2 rounded-lg text-xs ${isMe ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                                        <FileText size={16} className="shrink-0" />
                                        <span className="truncate">{file.original_file_name}</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              ) : null}
                              
                              <div>{item.message}</div>
                              <div className={`mt-1 text-[10px] text-right ${isMe ? 'text-blue-100' : 'text-slate-400'}`}>
                                {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="border-t border-slate-100 bg-white shrink-0 z-10 flex flex-col">
                  {replyingToMessage && (
                    <div className="flex items-center justify-between bg-slate-50 px-4 py-2 text-sm border-b border-slate-100">
                      <div className="border-l-4 border-blue-600 pl-3">
                        <p className="font-semibold text-blue-600 text-xs">Membalas pesan</p>
                        <p className="text-slate-600 truncate max-w-md">{replyingToMessage.message}</p>
                      </div>
                      <button
                        onClick={() => setReplyingToMessage(null)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  {selectedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border-b border-slate-100">
                      {selectedFiles.map((file, index) => {
                        const isImage = file.type.startsWith('image/');
                        const previewUrl = isImage ? URL.createObjectURL(file) : null;
                        return (
                          <div key={index} className="relative flex items-center gap-2 bg-white border border-slate-200 rounded p-1.5 pr-2 shadow-sm max-w-[200px]">
                            {isImage ? (
                              <img src={previewUrl!} alt="preview" className="w-8 h-8 object-cover rounded" />
                            ) : (
                              <div className="w-8 h-8 flex items-center justify-center bg-blue-50 text-blue-600 rounded">
                                <FileText size={16} />
                              </div>
                            )}
                            <span className="text-xs truncate flex-1 text-slate-600">{file.name}</span>
                            <button onClick={() => removeFile(index)} className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded">
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex gap-2 p-3 sm:p-4 items-center">
                    <input type="file" ref={imageInputRef} className="hidden" accept="image/png, image/jpeg, image/webp" multiple onChange={(e) => { if(e.target.files) { const files = Array.from(e.target.files); setSelectedFiles(prev => [...prev, ...files]); } e.target.value = ''; }} />
                    <input type="file" ref={documentInputRef} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple onChange={(e) => { if(e.target.files) { const files = Array.from(e.target.files); setSelectedFiles(prev => [...prev, ...files]); } e.target.value = ''; }} />
                    
                    <button onClick={() => imageInputRef.current?.click()} className="p-2 sm:p-2.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition" title="Lampirkan Gambar">
                      <ImagePlus size={20} />
                    </button>
                    <button onClick={() => documentInputRef.current?.click()} className="p-2 sm:p-2.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition" title="Lampirkan Dokumen">
                      <Paperclip size={20} />
                    </button>

                    <input
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSendMessage();
                      }}
                      disabled={sendMessageMutation.isPending}
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
                      placeholder="Ketik pesan..."
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={sendMessageMutation.isPending || (!message.trim() && selectedFiles.length === 0)}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    >
                      <Send size={16} />
                      <span className="hidden sm:inline">Kirim</span>
                    </button>
                  </div>
                </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-sm text-slate-400 p-8 text-center">
              <MessageSquare size={48} className="text-slate-200 mb-4" />
              <p>Pilih obrolan dari daftar di sebelah kiri untuk mulai mengirim pesan.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Pesan;
